import { Capacitor } from '@capacitor/core';
import * as Sentry from '@sentry/react';
import debug from 'debug';

import { RoisCredentials } from '../plugins/roisCredentials';
import { markCredentialPrompt } from './systemPrompts';

const log = debug('rois:utils:savedCredentials');

/**
 * Contraseña del operario guardada en el gestor del sistema.
 *
 * Solo tiene efecto dentro de la app nativa: en el navegador y en la PWA ya es
 * Chrome quien ofrece guardar, y duplicarlo sería pedirlo dos veces. Todas las
 * funciones son no-op fuera de nativo.
 *
 * Ninguna de estas funciones lanza. Un gestor de contraseñas es una comodidad;
 * si falla, el operario escribe la contraseña como hasta ahora, y lo último que
 * puede hacer es impedir un login.
 */

function isNative(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * La credencial que el gestor nos ha dado en esta sesión, si la hubo.
 *
 * 🔴 **Es lo que evita volver a pedir guardar lo que ya está guardado.** La doc
 * de Android dice que `CreatePasswordRequest` se usa tras un *registro* o un
 * *cambio* de contraseña, no tras cada inicio de sesión: `createCredential()`
 * es una acción explícita que SIEMPRE enseña UI y **no deduplica**. Si se llama
 * en cada login, el operario acaba viendo el diálogo de guardar cada vez que
 * entra — incluso habiendo entrado con la credencial que el propio gestor le
 * acababa de ofrecer. Decidir cuándo merece la pena preguntar es nuestro
 * trabajo, no suyo.
 *
 * Compararlo con lo que el gestor dio resuelve además el cambio de contraseña
 * sin ningún caso especial: si la de verdad cambió, el relleno automático falla
 * al entrar, el operario teclea la nueva, deja de coincidir con esta, y el
 * gestor recibe el aviso de actualizarla.
 */
let credentialKnownToManager: { username: string; password: string } | null =
  null;

/**
 * Petición en curso al gestor.
 *
 * El selector es UI del sistema y abrir dos a la vez no tiene sentido; esto
 * también absorbe el doble montaje de React en desarrollo.
 */
let pendingRequest: Promise<{ username: string; password: string } | null> | null =
  null;

/**
 * Ofrece guardar la credencial tras un login CORRECTO.
 *
 * Deliberadamente sin `await` en quien la llama: el diálogo lo pinta el
 * sistema por encima de la actividad, así que puede convivir con la
 * navegación posterior al login. Esperarlo dejaría al operario mirando la
 * pantalla de login hasta que decidiera, y en campo eso es peor que el
 * problema que resuelve.
 */
export function offerToSaveCredentials(
  username: string,
  password: string
): void {
  if (!isNative()) return;
  if (!username || !password) return;

  // Entró con exactamente lo que el gestor le ofreció: no hay nada nuevo que
  // guardar y preguntarlo es puro ruido.
  if (
    credentialKnownToManager?.username === username &&
    credentialKnownToManager?.password === password
  ) {
    log('la credencial ya venía del gestor; no se vuelve a ofrecer');
    return;
  }

  // Mientras la hoja de Google esté encima, los avisos y las peticiones de
  // permisos esperan (ver `systemPrompts`): con ella en pantalla las
  // respuestas de Android no llegaban a la app (medido en la W el 25/09).
  markCredentialPrompt(true);
  void RoisCredentials.save({ username, password })
    .then((result) => {
      log('resultado de guardar credencial: %s', result.reason);
      if (result.saved) {
        // Un segundo login en la misma sesión ya no tiene por qué preguntar.
        credentialKnownToManager = { username, password };
      }
      // `cancelled` es una respuesta del operario, no una incidencia.
      if (result.reason === 'error') {
        Sentry.captureMessage('No se pudo guardar la credencial en el gestor', {
          level: 'warning',
        });
      }
    })
    .catch((error) => {
      // Llega aquí si el plugin no existe (APK viejo con bundle OTA nuevo).
      log('plugin de credenciales no disponible: %o', error);
    })
    .finally(() => markCredentialPrompt(false));
}

/**
 * Pide al gestor una credencial guardada para rellenar el login.
 *
 * Se ofrece **cada vez que aparece la pantalla de login**, también tras cerrar
 * sesión. Llegó a estar limitado a una vez por arranque para no ofrecerle
 * volver a entrar a quien acababa de salir, pero en una tablet compartida el
 * relevo de turno es justo el momento en que más falta hace: el selector es lo
 * que permite al siguiente operario elegir SU credencial.
 *
 * Devuelve `null` siempre que no haya nada que rellenar: fuera de nativo, si no
 * hay credenciales, si el operario cierra el selector, si el plugin no está o
 * si falla. Quien llama solo tiene que mirar si hay algo.
 */
export async function requestSavedCredentials(): Promise<{
  username: string;
  password: string;
} | null> {
  if (!isNative()) return null;
  if (pendingRequest) return pendingRequest;

  pendingRequest = (async () => {
    try {
      const result = await RoisCredentials.get();
      log('resultado de recuperar credencial: %s', result.reason);
      if (result.found) {
        credentialKnownToManager = {
          username: result.username,
          password: result.password,
        };
        return { username: result.username, password: result.password };
      }
      if (result.reason === 'error') {
        Sentry.captureMessage('No se pudo recuperar la credencial del gestor', {
          level: 'warning',
        });
      }
      return null;
    } catch (error) {
      // El plugin puede no existir en un APK anterior a esta versión nativa.
      log('plugin de credenciales no disponible: %o', error);
      return null;
    }
  })();

  try {
    return await pendingRequest;
  } finally {
    pendingRequest = null;
  }
}
